import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import LandingPage from './pages/LandingPage';
import GetPrint from './pages/GetPrint';
import MyAccountPage from './pages/MyAccountPage';
import ShopDirectGetPrint from './pages/ShopDirectGetPrint';
import WhatsAppGetPrint from './pages/WhatsAppGetPrint';
import ShopWhatsAppDirectPrint from './pages/ShopWhatsAppDirectPrint';
import PrintBazaarPage from './pages/PrintBazaarPage';
import PrintBazaarDetailsPage from './pages/PrintBazaarDetailsPage';
import AdminPanel from './pages/AdminPanel';
import ShopRegistrationPage from './pages/ShopRegistrationPage';
import ShopRegistrationSuccessPage from './pages/ShopRegistrationSuccessPage';
import { useAuthStore } from './store/authStore';
import AdvertisingPage from './pages/AdvertisingPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsAndConditions from './pages/TermsAndConditions';
import SpiralBindingForm from './pages/SpiralBindingForm';
import SoftwarePage from './pages/SoftwarePage';
import { Toaster } from 'react-hot-toast';
import { CameraModalProvider, useCameraModal } from './context/CameraModalContext';
import CameraCaptureModal from './components/print-steps/CameraCaptureModal';
import KYCFormPage from './pages/KYCFormPage';
import SitemapPage from './pages/SitemapPage';
import NotFoundPage from './pages/NotFoundPage';
import RouteSchemaMarkup from './components/RouteSchemaMarkup';
import NagpurPage from './pages/cities/nagpur';
import CitiesPage from './pages/cities/index';

// Admin route guard component
const AdminRoute: React.FC<{ element: React.ReactNode }> = ({ element }) => {
  const { user, isAdmin, loading } = useAuthStore();
  
  console.log('AdminRoute rendered with:', { userId: user?.id, isAdmin, loading });
  
  // If the user is confirmed as an admin, show the admin panel
  if (isAdmin) {
    console.log('User is confirmed admin, rendering admin panel');
    return <>{element}</>;
  }
  

  
  // If we're still loading, show loading state
  // This prevents premature redirects during authentication checks
  if (loading) {
    console.log('Still loading authentication state, showing loading indicator');
    return <div className="flex justify-center items-center h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      <p className="ml-3 text-gray-600">Verifying access...</p>
    </div>;
  }
  
  // Otherwise (no user or loading complete and not admin), redirect to home
  console.log('No user or not admin, redirecting to home');
  return <Navigate to="/" replace />;
};

// Camera modal component that renders at root level
const GlobalCameraModal: React.FC = () => {
  const { isOpen, closeCameraModal, handleCapture } = useCameraModal();

  if (!isOpen) return null;

  return (
    <CameraCaptureModal
      onClose={closeCameraModal}
      onCapture={handleCapture}
    />
  );
};

function AppContent() {
  const { checkSession } = useAuthStore();
  
  useEffect(() => {
    // Check for existing session on app load
    checkSession();
  }, []);
  
  return (
    <>
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#fff',
            color: '#333',
          },
        }}
      />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/get-print" element={<GetPrint />} />
        <Route path="/ads" element={<AdvertisingPage />} />
        <Route path="/get-print/shop/:shopId" element={<ShopDirectGetPrint />} />
        <Route path="/get-print/wa/:sessionId" element={<WhatsAppGetPrint />} />
        <Route path="/get-print/shop/wa/:sessionId/:shopId" element={<ShopWhatsAppDirectPrint />} />
        <Route path="/my-account" element={<MyAccountPage />} />
        <Route path="/print-bazaar" element={<PrintBazaarPage />} />
        <Route path="/print-bazaar/:documentId" element={<PrintBazaarDetailsPage />} />
        <Route path="/admin" element={<AdminRoute element={<AdminPanel />} />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy/> } />
        <Route path="/terms-and-conditions" element={<TermsAndConditions/>} />
        <Route path="/shop-registration" element={<ShopRegistrationPage />} />
        <Route path="/shop-registration-success" element={<ShopRegistrationSuccessPage />} />
        {/* <Route path="/spiral-binding" element={<SpiralBindingForm />} /> */}
        <Route path="/software" element={<SoftwarePage />} />
        <Route path="/kyc-form" element={<KYCFormPage />} />
        <Route path="/sitemap" element={<SitemapPage />} />
        <Route path="/cities" element={<CitiesPage />} />
        <Route path="/cities/nagpur" element={<NagpurPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>

      {/* Camera Modal rendered at root level */}
      <GlobalCameraModal />
    </>
  );
}

function App() {
  return (
    <CameraModalProvider>
      <Router>
        <RouteSchemaMarkup />
        <AppContent />
      </Router>
    </CameraModalProvider>
  );
}

export default App;
